import type { ShortVideo } from '~/composables/shorts/useFetchShorts';
import type { ShortsFilters } from '~/core/entities/property/filters';

import { useLastVideoState } from '~/composables/shorts/useLastVideoState';
import { usePropertyIntentPrefetch } from '~/composables/shorts/usePropertyIntentPrefetch';
import { useShortsEntryContext } from '~/composables/shorts/useShortsEntryContext';
import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { shortsFiltersToListingQuery } from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { prefetchShakaChunkOnIdle } from '~/composables/useVideoWarmup';

import {
	blessSharedVideoElement,
	setIOSMsePreference
} from '~/components/PropertyReelsFeed/useIOSSharedVideo';

export const SHORTS_ENTRY_SOURCE = {
	CLUSTER: 'cluster'
} as const;

export type ShortsEntrySource =
	(typeof SHORTS_ENTRY_SOURCE)[keyof typeof SHORTS_ENTRY_SOURCE];

const SHORTS_ENTRY_SOURCE_QUERY_KEY = 'entrySource';

export interface OpenShortsFeedEntryContext {
	items: ShortVideo[];
	filters: ShortsFilters;
	page: number;
	perPage?: number;
	hasMore: boolean;
}

interface OpenShortsFeedParams {
	commercialId: string;
	clickedIndex: number;
	entryContext?: OpenShortsFeedEntryContext | null;
	queryFilters?: ShortsFilters;
	queryParams?: Record<string, string>;
	entrySource?: ShortsEntrySource;
}

function withEntrySource(
	query: Record<string, string>,
	entrySource?: ShortsEntrySource
): Record<string, string> {
	if (!entrySource) return query;

	return {
		...query,
		[SHORTS_ENTRY_SOURCE_QUERY_KEY]: entrySource
	};
}

export function isShortsEntrySource(
	query: Record<string, unknown>,
	entrySource: ShortsEntrySource
): boolean {
	const value = query[SHORTS_ENTRY_SOURCE_QUERY_KEY];
	return Array.isArray(value)
		? value[0] === entrySource
		: value === entrySource;
}

export function buildShortsFeedDestination(
	commercialId: string,
	query: Record<string, string> = {},
	entrySource?: ShortsEntrySource
): string {
	const search = new URLSearchParams(
		withEntrySource(query, entrySource)
	).toString();
	return `/shorts/${commercialId}${search ? `?${search}` : ''}`;
}

export function useOpenShortsFeed() {
	const lastVideoState = useLastVideoState();
	const shortsEntryContext = useShortsEntryContext();

	// Any surface that can open the shorts feed is a strong signal the Shaka
	// chunk will be needed — warm it while this page idles. The iOS MSE
	// preference must be set first: outside the shorts page it defaults to
	// false, which would misclassify MSE-capable iOS as native playback and
	// skip the warm for users who will in fact load Shaka.
	setIOSMsePreference(useFeatureFlags().ft_shorts_ios_mse);
	prefetchShakaChunkOnIdle();

	const { prefetchPropertyOnIntent } = usePropertyIntentPrefetch();

	function openShortsFeed({
		commercialId,
		clickedIndex,
		entryContext,
		queryFilters,
		queryParams,
		entrySource
	}: OpenShortsFeedParams): void {
		const path = `/shorts/${commercialId}`;

		// Confirmed intent: the reel page will fetch this exact property right
		// after the SPA navigation — start the request now so it runs in
		// parallel with the route transition.
		prefetchPropertyOnIntent(commercialId);

		lastVideoState.save(commercialId, clickedIndex);
		blessSharedVideoElement();

		if (entryContext) {
			shortsEntryContext.value = entryContext;
		}

		if (queryParams) {
			navigateTo({ path, query: withEntrySource(queryParams, entrySource) });
			return;
		}

		const filters = queryFilters ?? entryContext?.filters;
		if (!filters) {
			if (entrySource) {
				navigateTo({ path, query: withEntrySource({}, entrySource) });
			} else {
				navigateTo(path);
			}
			return;
		}

		navigateTo({
			path,
			query: withEntrySource(shortsFiltersToListingQuery(filters), entrySource)
		});
	}

	return { openShortsFeed };
}
